import React, { useEffect, useMemo, useState } from 'react'
import {
	Col,
	Collapse,
	Row,
	Space,
	Typography,
	Pagination,
	Result,
	Drawer,
	Button,
	Radio,
	RadioChangeEvent,
	Checkbox,
} from 'antd'
import { useHistory, useLocation } from 'react-router-dom'
import { FilterOutlined, FrownOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'

import QueryString from 'query-string'
import ISO6391 from 'iso-639-1'

import SpinLoader from '../../helpers/SpinLoader'
import BookingModal from '../../helpers/BookingModal/BookingModal'
import CustomTree from '../../helpers/CustomTree/CustomTree'

import ExpertItem from './components/ExpertItem'
import { useExpertsContext } from '../../../context/experts'

import { ILanguage, TCountry, TExpert, TExpertsQuery } from '../../../context/types'

import { useChannelContext } from '../../../context/channel'
import { useAuthContext } from '../../../context/auth'
import { callApi } from '../../../context/api'

import './experts.css'

const { Title } = Typography
const { Panel } = Collapse

function Experts() {
	const { experts, actions } = useExpertsContext()
	const { auth } = useAuthContext()

	const history = useHistory()
	const location = useLocation()

	const { channel, actions: channelActions } = useChannelContext()

	const [isModalVisible, setIsModalVisible] = useState(false)
	const [isLoaded, setIsLoaded] = useState(false)
	const [activeExpert, setActiveExpert] = useState<TExpert>()
	const [activeKey, setActiveKey] = useState<string | string[]>()
	const [openFilter, setOpenFilter] = useState<boolean>(false)
	const [urlQueryString, setUrlQueryString] = useState({ search: null, isInitial: true })
	const [countries, setCountries] = useState<TCountry[]>([])
	const [languages, setLanguages] = useState<ILanguage[]>([])

	const expertFilter = useMemo(() => {
		const currentChannel = channel.filterChannels?.find(({ id }) => id === channel.selectedChannelId)
		return currentChannel?.expert_filter
	}, [channel.selectedChannelId, channel.filterChannels])

	useEffect(() => {
		const { q } = QueryString.parse(location.search)
		if (q !== urlQueryString.search && channel.selectedChannelId) {
			setUrlQueryString({ search: q, isInitial: false })
		}
	}, [location, channel.selectedChannelId])

	useEffect(() => {
		if (!urlQueryString.isInitial) {
			const parsedSearch = parseSearch()
			actions.getExperts(parsedSearch)
		}
	}, [urlQueryString])

	useEffect(() => {
		if ((!isLoaded && channel.selectedChannelId) || !auth.token) {
			const parsedSearch = parseSearch()

			actions.getExperts({
				...parsedSearch,
			})
			setIsLoaded(true)
		}
	}, [channel.selectedChannelId])

	useEffect(() => {
		if (channel.selectedChannelId || !channel.loading) {
			callApi('/countries/experts', { query: { channelId: channel.selectedChannelId } }).then((response) => {
				if (response.ok) {
					setCountries(response.payload.data)
				}
			})

			callApi('/languages/experts', { query: { channelId: channel.selectedChannelId } }).then((response) => {
				if (response.ok) {
					setLanguages(response.payload.data)
				}
			})
		}
	}, [channel.selectedChannelId, channel.loading])

	useEffect(() => {
		const defaultActiveKey = []
		if (experts?.filter?.countries?.length || expertFilter?.countries?.length) defaultActiveKey.push('panel_locations')
		if (experts?.filter?.languages?.length || expertFilter?.languages?.length) defaultActiveKey.push('panel_languages')
		if (experts?.filter?.channel) defaultActiveKey.push('panel_channels')
		setActiveKey(defaultActiveKey)
	}, [experts?.filter, expertFilter])

	function parseSearch() {
		if (history.location.search || channel.selectedChannelId) {
			const {
				countries: queryCountries,
				keywords: queryKeywords,
				languages: queryLanguages,
				channel: queryChannel,
				favorites,
				q,
			}: TExpertsQuery = QueryString.parse(history.location.search, {
				arrayFormat: 'comma',
				parseNumbers: true,
			})

			const countries = expertFilter?.countries?.length ? undefined : getQueryValue(queryCountries)
			const keywords = getQueryValue(queryKeywords)
			const languages = expertFilter?.languages?.length
				? getQueryValue(queryLanguages)?.filter((language) => !expertFilter.languages.some((iso) => iso === language))
				: getQueryValue(queryLanguages)
			const filterChannel = queryChannel
				? channelActions.updateSelectedChannelId(queryChannel)
				: channel.selectedChannelId

			return {
				countries,
				keywords,
				languages,
				channel: filterChannel || undefined,
				q,
				favorites,
			}
		}
		return {}
	}

	const onCheckCountries = (checkedKeys: any) => {
		actions.getExperts({
			...experts?.filter,
			keywords: [],
			countries: checkedKeys,
		})
	}

	const onCheckLanguages = (checkedKeys: any) => {
		actions.getExperts({
			...experts?.filter,
			keywords: [],
			languages: expertFilter?.languages?.length
				? checkedKeys.filter((language: string) => !expertFilter.languages.some((iso) => iso === language))
				: checkedKeys,
		})
	}

	const onChannelChange = (e: RadioChangeEvent) => {
		const channelId = e.target.value
		const newFilter = channel.filterChannels?.find(({ id }) => id === channelId).expert_filter
		channelActions.setSelectedChannelId(channelId)
		actions.getExperts(
			{
				...experts?.filter,
				languages: newFilter?.languages?.length
					? experts?.filter.languages?.filter(
							(language: string) => !newFilter.languages.some((iso) => iso === language)
					  )
					: experts?.filter.languages,
				keywords: [],
				channel: channelId,
			},
			0
		)
	}

	const onFavoritesToggle = () => {
		const filter = { ...experts?.filter }

		if (filter.favorites) {
			delete filter.favorites
		} else {
			filter.favorites = true
		}

		actions.getExperts(filter, 0)
	}

	const onRequestCall = (expert: TExpert) => () => {
		setActiveExpert(expert)
		setIsModalVisible(true)
	}

	const handleCancel = () => {
		setIsModalVisible(false)
	}

	const setPage = (page: number, pageSize: number) => {
		if (experts.page !== page - 1) actions.setPage(page - 1)
		if (experts.pageSize !== pageSize) actions.setPageSize(pageSize)
	}

	const getLanguagesCheckedValues = () => {
		const languages = experts.filter?.languages || []
		const channelLanguages = expertFilter?.languages || []
		return Array.from(new Set([...languages, ...channelLanguages]))
	}

	const filterComponent = (
		<Collapse
			ghost
			expandIconPosition="right"
			bordered={false}
			expandIcon={expandIcon}
			className="experts-collapse"
			activeKey={activeKey}
			onChange={setActiveKey}
		>
			<Panel header={<Title level={5}>Locations</Title>} key="panel_locations">
				<CustomTree
					treeData={getCountriesTreeData(countries)}
					onCheck={onCheckCountries}
					checkedKeys={expertFilter?.countries || experts.filter?.countries}
					disabled={!!expertFilter?.countries}
				/>
			</Panel>
			<Panel header={<Title level={5}>Languages</Title>} key="panel_languages">
				<CustomTree
					treeData={getLanguagesTreeData(languages, expertFilter?.languages)}
					onCheck={onCheckLanguages}
					checkedKeys={getLanguagesCheckedValues()}
				/>
			</Panel>
			{channel.filterChannels?.length > 1 && channel.selectedChannelId && (
				<Panel header={<Title level={5}>Channels</Title>} key="panel_channels" collapsible="header">
					<Radio.Group onChange={onChannelChange} value={channel.selectedChannelId}>
						<Space direction="vertical">
							{channel.filterChannels.map((filterChannel) => (
								<Radio key={filterChannel.id} value={filterChannel.id}>
									{filterChannel.name}
									{filterChannel.name === 'channelWise' ? ' (beta)' : ''}
								</Radio>
							))}
						</Space>
					</Radio.Group>
				</Panel>
			)}

			{auth.user && (
				<div style={{ marginTop: 20, paddingLeft: 16 }}>
					<Checkbox checked={Boolean(experts?.filter?.favorites)} onClick={onFavoritesToggle}>
						Favorites Only
					</Checkbox>
				</div>
			)}
		</Collapse>
	)

	return (
		<SpinLoader loading={!!experts.loading}>
			<div className="experts-filters-btn-block">
				<Button
					type="primary"
					ghost
					block
					icon={<FilterOutlined />}
					className="experts-filters-btn"
					onClick={() => setOpenFilter(true)}
				>
					Open filter
				</Button>
			</div>
			<Row wrap={false} className="experts-row-container">
				<Col flex="350px" style={{ marginRight: 30 }} className="experts-col-filter no-user-select">
					{filterComponent}
				</Col>
				<Col flex="auto">
					<Space direction="vertical" style={{ width: '100%' }}>
						{!!experts.experts.length && (
							<Space direction="vertical" style={{ maxWidth: 'calc(100vw - 20px)', width: '100%' }}>
								{experts.experts.map((expert: TExpert) => (
									<ExpertItem key={expert.id} expert={expert} onRequestCall={onRequestCall(expert)} />
								))}
								<Pagination
									total={experts.count}
									pageSize={experts.pageSize}
									current={experts.page + 1}
									defaultCurrent={1}
									onChange={setPage}
								/>
							</Space>
						)}
					</Space>
					{!experts.loading && !experts.experts.length && (
						<Result
							icon={<FrownOutlined />}
							title="There are no experts that fit your criteria"
							extra={
								<p>
									We are always onbarding new experts <br /> For now, try widening your search
								</p>
							}
						/>
					)}
				</Col>
			</Row>
			<BookingModal visible={isModalVisible} onCancel={handleCancel} expert={activeExpert} />
			<Drawer title="Filter" placement="left" onClose={() => setOpenFilter(false)} visible={openFilter} width={350}>
				{filterComponent}
			</Drawer>
		</SpinLoader>
	)
}

export default Experts

// Helpers

const expandIcon = ({ isActive }: { isActive?: boolean }) => (
	<span>
		<FontAwesomeIcon
			icon={faChevronDown}
			color="#434343"
			fontWeight="900"
			opacity="0.5"
			rotation={!isActive ? undefined : 180}
		/>
	</span>
)

// Utils

// tslint:disable

function getCountriesTreeData(countries: TCountry[]) {
	return countries.map((country: TCountry) => ({
		title: country.name,
		key: country.id,
	}))
}

function getLanguagesTreeData(languages: ILanguage[], channelLanguages?: string[]) {
	return languages.map((language: ILanguage) => ({
		title: ISO6391.getName(language.iso),
		key: language.iso,
		disabled: channelLanguages?.some((iso) => iso === language.iso),
	}))
}

function getQueryValue(params: any) {
	return Array.isArray(params) ? params : params ? [params] : undefined
}
